import type { FC } from 'react';
import { Button, IconButton, SideNav, SideNavItems, SideNavLink, SideNavMenu, SideNavMenuItem, Tag } from '@carbon/react';
import { ArrowRight, ArrowsHorizontal } from '@carbon/icons-react';
import type { CarbonIconType } from '@carbon/icons-react';

import { UsoDoEspaco } from '../padroes/UsoDoEspaco';
import { useDoPonto } from '../padroes/useDoMd';
import { FotoDoPerfil } from './FotoDoPerfil';
import styles from './Casca.module.scss';

// OS MÓDULOS DO PERFIL — o painel da esquerda do UI Shell.
//  - No alto, o perfil aberto (foto, nome e o perfil REAL): quem está num módulo sempre sabe de qual
//    artista é o que está vendo (pedido do Lucas, 27/09/2026).
//  - No meio, um `SideNavLink` por módulo e um `SideNavMenu` por grupo (o Desenvolvimento).
//  - No pé, o plano da conta e o espaço usado, com a porta para os planos.
// Recolhido (do `md` para cima; no tablet, sempre), é o rail do Carbon: só os ícones, e ele abre por
// cima ao passar o mouse ou ao receber o foco. No celular, abre pelo ☰, por cima do conteúdo, e ali
// o botão de troca, ao lado do perfil aberto, leva aos outros perfis (a faixa dos perfis é do tablet
// e do computador). Ele aparece sempre, mesmo com um perfil só: sem ele, o celular não tinha caminho
// de volta para os perfis (pedido do Lucas, 04/10/2026).

export interface PaginaDoGrupo {
  chave: string;
  rotulo: string;
}

export interface ModuloDaNavegacao {
  chave: string;
  rotulo: string;
  icone: CarbonIconType;
  /** Um grupo: o módulo abre um menu com estas páginas, em vez de ser uma página. */
  filhos?: PaginaDoGrupo[];
}

export interface PerfilDaNavegacao {
  nome: string;
  imagem: string | null;
  apoio: string;
}

export interface PlanoDaNavegacao {
  rotulo: string;
  tipo: 'blue' | 'warm-gray' | 'gray';
  /** `semCota`: o plano grátis, sem espaço para arquivos (um texto neutro, sem a barra vermelha). */
  espaco: { rotulo: string; percentual: number; cheio: boolean; semCota?: boolean } | null;
  verPlano: () => void;
  /** Só no plano grátis. */
  assinar?: () => void;
}

export const NavegacaoDoPerfil: FC<{
  aberta: boolean;
  aoFechar: () => void;
  rotulo: string;
  modulos: ModuloDaNavegacao[];
  ativo: (chave: string) => boolean;
  abrir: (chave: string) => void;
  perfil?: PerfilDaNavegacao | null;
  plano?: PlanoDaNavegacao | null;
  /** Do `md` para cima: só os ícones (o rail do Carbon). */
  recolhido?: boolean;
  /** Abaixo do `md`: o caminho para os outros perfis (no tablet e no computador, a faixa faz isso). */
  trocarDePerfil?: { rotulo: string; abrir: () => void };
}> = ({ aberta, aoFechar, rotulo, modulos, ativo, abrir, perfil, plano, recolhido, trocarDePerfil }) => {
  const ir = (chave: string) => (e: React.MouseEvent) => { e.preventDefault(); abrir(chave); aoFechar(); };
  // Do md para cima o painel está sempre à vista: aberto para o Carbon também, senão os links saem
  // da ordem do Tab (`tabindex=-1`) e o menu só se alcança com o rato. Recolhido, é o rail (que
  // deixa os links na ordem do Tab e abre ao receber o foco).
  const fixo = useDoPonto('md');
  const rail = fixo && !!recolhido;
  return (
    <SideNav
      aria-label={rotulo}
      expanded={rail ? false : aberta || fixo}
      isRail={rail}
      onOverlayClick={aoFechar}
      className={styles.menu}
    >
      {/* O perfil aberto só no celular (no computador, a faixa mostra qual é, e o menu fica limpo). */}
      {perfil && !fixo && (
        <div className={styles.perfilDoMenu}>
          <FotoDoPerfil nome={perfil.nome} imagem={perfil.imagem} className={styles.fotoDoMenu} />
          <span className={styles.quemDoMenu}>
            <span className={styles.nomeDoMenu}>{perfil.nome}</span>
            <span className={styles.apoioDoMenu}>{perfil.apoio}</span>
          </span>
          {!fixo && trocarDePerfil && (
            <IconButton kind='ghost' size='sm' align='left' label={trocarDePerfil.rotulo} onClick={() => { aoFechar(); trocarDePerfil.abrir(); }}>
              <ArrowsHorizontal />
            </IconButton>
          )}
        </div>
      )}
      <SideNavItems>
        {modulos.map((m) => {
          if (m.filhos) {
            // A filha acesa é a de endereço mais fundo: em /marketing/redes/plano, o Plano de redes, e
            // não a Gestão das Redes (/marketing/redes), que também é começo do endereço. O grupo
            // acende também na página dele (o Início do Marketing).
            const acesa = m.filhos.filter((f) => ativo(f.chave)).sort((a, b) => b.chave.length - a.chave.length)[0]?.chave;
            const aceso = !!acesa || ativo(m.chave);
            return (
              // O grupo abre aceso quando se está numa das páginas dele.
              <SideNavMenu key={m.chave} renderIcon={m.icone} title={m.rotulo} isActive={aceso} defaultExpanded={aceso}>
                {m.filhos.map((f) => (
                  <SideNavMenuItem
                    key={f.chave}
                    href={`#${f.chave}`}
                    isActive={f.chave === acesa}
                    aria-current={f.chave === acesa ? 'page' : undefined}
                    onClick={ir(f.chave)}
                  >
                    {f.rotulo}
                  </SideNavMenuItem>
                ))}
              </SideNavMenu>
            );
          }
          return (
            <SideNavLink
              key={m.chave || 'inicio'}
              renderIcon={m.icone}
              isActive={ativo(m.chave)}
              aria-current={ativo(m.chave) ? 'page' : undefined}
              href={m.chave ? `#${m.chave}` : '#inicio'}
              onClick={ir(m.chave)}
            >
              {m.rotulo}
            </SideNavLink>
          );
        })}
      </SideNavItems>
      {plano && (
        <div className={styles.planoDoMenu}>
          <div className={styles.linhaDoPlano}>
            <span className={styles.rotuloDoPlano}>Seu plano</span>
            <Tag size='sm' type={plano.tipo}>{plano.rotulo}</Tag>
          </div>
          {plano.espaco && (plano.espaco.semCota
            ? <p className={styles.semCota}>{plano.espaco.rotulo}</p>
            : <UsoDoEspaco rotulo='Espaço da conta' percentual={plano.espaco.percentual} cheio={plano.espaco.cheio} texto={plano.espaco.rotulo} />)}
          {plano.assinar
            ? <Button kind='primary' size='md' renderIcon={ArrowRight} onClick={() => { aoFechar(); plano.assinar?.(); }} className={styles.botaoDoPlano}>Assinar Maestra PRO</Button>
            : <Button kind='ghost' size='md' renderIcon={ArrowRight} onClick={() => { aoFechar(); plano.verPlano(); }} className={styles.botaoDoPlano}>Ver plano e uso</Button>}
        </div>
      )}
    </SideNav>
  );
};
